import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import TextFieldController from '@/components/inputs/TextFieldController'
import type { BatteryBinding } from '@/features/controller/configuration/models/resourceConfigSchema'
import type { BindingFormSchemaType } from '@/features/resource/components/localControl/schemas'
import { getBindingFormSchema } from '@/features/resource/components/localControl/schemas'

export type BindingFormProps = Readonly<{
  binding: BatteryBinding
  isSaving: boolean
  onSave: (data: BindingFormSchemaType) => Promise<void> | void
  onCancel: () => void
}>

export function BindingForm({ binding, isSaving, onSave, onCancel }: BindingFormProps) {
  const { t } = useTranslation()

  const form = useForm<BindingFormSchemaType>({
    defaultValues: {
      batteryRef: binding.batteryRef,
    },
    resolver: zodResolver(getBindingFormSchema(t)),
  })

  return (
    <FormProvider {...form}>
      <Stack
        noValidate
        aria-label={t('resources.local_control.binding.edit_label')}
        component="form"
        gap={2}
        onSubmit={form.handleSubmit(onSave)}
      >
        <TextFieldController<BindingFormSchemaType>
          required
          disabled={isSaving}
          label={t('resources.local_control.binding.battery_ref')}
          name="batteryRef"
        />
        <Box>
          <Stack direction="row" gap={2}>
            <CustomButton disabled={isSaving} type="submit" variant="contained">
              {isSaving ? t('resources.local_control.binding.saving') : t('common.button.save')}
            </CustomButton>
            <CustomButton disabled={isSaving} variant="outlined" onClick={onCancel}>
              {t('common.button.cancel')}
            </CustomButton>
          </Stack>
        </Box>
      </Stack>
    </FormProvider>
  )
}
